// src/Dashboard/pages/user/News/components/NewsReadDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowsAlt,
  FaCrop,
  FaTimes,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../services/newsPhotoServices";

import {
  FALLBACK_NEWS_IMAGE,
  formatNewsDate,
  getAuthorName,
  getNewsDate,
  onImgError,
  sanitizeLight,
} from "../utils/newsDashboardUtils";

export default function NewsReadDrawer({ isOpen, news, onClose }) {
  const [fit, setFit] = useState("cover");
  const [objectPosition, setObjectPosition] = useState("center");

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const cover = useMemo(() => {
    return toAbsolutePhotoUrl(news?.image_url || "") || FALLBACK_NEWS_IMAGE;
  }, [news?.image_url]);

  const safeContent = useMemo(() => {
    return sanitizeLight(news?.content || "") || "<p>Contenu vide.</p>";
  }, [news?.content]);

  if (!isOpen) return null;

  const authorName = getAuthorName(news);
  const date = getNewsDate(news);

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;

    if (naturalHeight > naturalWidth) {
      setFit("contain");
      setObjectPosition("center 12%");
    } else {
      setFit("cover");
      setObjectPosition("center");
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Lecture actualité
            </p>

            <h2 className="line-clamp-2 text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {news?.title || "Sans titre"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="relative bg-slate-100 dark:bg-slate-950">
            <div className="h-[320px] sm:h-[420px]">
              <img
                src={cover}
                alt={news?.title || "Illustration actualité"}
                className={`h-full w-full ${
                  fit === "contain" ? "object-contain" : "object-cover"
                }`}
                style={{ objectPosition }}
                onLoad={handleImageLoad}
                onError={onImgError}
              />
            </div>

            <button
              type="button"
              onClick={() =>
                setFit((current) => (current === "cover" ? "contain" : "cover"))
              }
              title={fit === "contain" ? "Recadrer" : "Ajuster"}
              className="absolute bottom-4 right-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-white dark:bg-slate-900/90 dark:text-slate-200 dark:ring-slate-700"
            >
              {fit === "contain" ? <FaCrop /> : <FaArrowsAlt />}
            </button>
          </div>

          <div className="p-4 sm:p-6">
            <div className="mb-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Publié le{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {formatNewsDate(date)}
                </span>
                {authorName && (
                  <>
                    {" "}
                    par{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {authorName}
                    </span>
                  </>
                )}
              </p>

              {!news?.is_active && (
                <span className="mt-3 inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                  Actualité masquée
                </span>
              )}
            </div>

            <div
              className="prose prose-slate max-w-none dark:prose-invert prose-img:rounded-xl prose-a:text-sky-600 dark:prose-a:text-sky-400 prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: safeContent }}
            />
          </div>
        </div>
      </aside>
    </div>
  );
}